// Helper for safer unique IDs
const generateId = () => 
  `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

// === Shared two-sided model (portal foundation) ==========================
// Buildings + equipment + service history are SHARED assets keyed by a
// memberOrgs access list, not siloed under one org. An "account" is the
// facility company / customer grouping above buildings.

// The facility company / customer grouping (was "customer").
//   type "facility" = a facility org on the platform owns this account
//   type "managed"  = a contractor created it for a facility NOT on the platform
export const createAccount = (name) => ({
  id: generateId(),
  name,
  type: "managed", // "facility" | "managed"
  facilityOrgId: "", // set when a facility org owns/claims this account
  ownerOrgId: "", // the org that created it (set on write)
  memberOrgs: [], // orgs that can access this account's buildings (set on write)
  createdByOrg: "",
  createdAt: new Date().toISOString(),
});

// A physical site/building (was "location"). Equipment + service history hang
// off it (flat collections with buildingId). memberOrgs controls who can
// read/write; a contractor is linked by adding their orgId here (per-building).
export const createBuilding = (name) => ({
  id: generateId(),
  accountId: "",
  name,
  address: "",
  street: "",
  city: "",
  state: "",
  zip: "",
  building: "", // suite / building label within the site
  accessNotes: "",
  poc1Name: "",
  poc1Phone: "",
  poc2Name: "",
  poc2Phone: "",
  ownerOrgId: "", // org that created the building
  memberOrgs: [], // orgs allowed to read/write (owner + linked) — set on write
  createdByOrg: "",
  createdAt: new Date().toISOString(),
});

export const createCustomer = (name) => ({
  id: generateId(),
  name,
  // Main contact info — shown on the Customers list info card.
  phone: "",
  email: "",
  street: "",
  city: "",
  state: "",
  zip: "",
  // Billing block — separate so a customer can have one site address but
  // pay from another.
  billingStreet: "",
  billingCity: "",
  billingState: "",
  billingZip: "",
  billingContactName: "",
  billingContactPhone: "",
  billingContactEmail: "",
  notes: "",
  // Tax-exempt status. When taxExempt is true, NO tax is computed on any
  // quote, bill, or invoice for this customer — the tax block on PDFs
  // still renders (regulatory norm) but shows $0.00 with the cert number
  // citation. Cert + reason are mandatory metadata when the flag is on.
  taxExempt: false,
  taxExemptCert: "", // e.g. "TX Sales Tax Exemption #12345678"
  taxExemptCertExpires: "", // YYYY-MM-DD, optional ("" = no expiry / unknown)
  taxExemptReason: "", // "Govt entity", "Resale", "Nonprofit 501(c)(3)", etc.
  // QuickBooks linkage (Phase 1A of QB integration). qbListId is the
  // permanent unique key from QB Desktop / QBO. Names are NOT safe keys
  // (renames break them; FullName is QB's only uniqueness constraint).
  // qbSyncSource records which side originated the doc: "qbd" / "qbo" /
  // "manual". qbEditSequence is QB's optimistic-concurrency token —
  // needed for any future write-back to QB. qbClassId is QB Class
  // tracking (separate axis from Customer:Job hierarchy; relevant when
  // the contractor tracks site/department P&L by Class). Default empty
  // so manually-created customers stay unaffected.
  qbListId: "",
  qbSyncSource: "manual", // "qbd" | "qbo" | "manual"
  qbEditSequence: "",
  qbClassId: "",
  locations: [],
  contacts: [],
  createdAt: new Date().toISOString(),
});

export const createLocation = (name) => ({
  id: generateId(),
  name,
  address: "",
  street: "",
  city: "",
  state: "",
  zip: "",
  building: "",
  accessNotes: "",
  poc1Name: "",
  poc1Phone: "",
  poc2Name: "",
  poc2Phone: "",
  // QuickBooks linkage (Phase 1A). QB stores locations TWO ways:
  //   - "job"    — a sub-customer (Customer:Job hierarchy). Has its own
  //                ListID; individually billable. The structurally
  //                correct pattern. QBO collapses all locations to this.
  //   - "shipto" — an "additional address" line on the parent customer.
  //                NO stable ID — keyed by composite (parent ListID +
  //                ship-to Name). Renames break the link. QBD-only.
  //   - "none"   — manually-created location, no QB linkage.
  // qbAddressRaw captures the address as it landed from QB so we can
  // audit dedup decisions later. qbClassId is QB Class tracking — at
  // location level when the contractor splits P&L per site.
  qbLinkType: "none",
  qbJobListId: "",
  qbParentListId: "",
  qbShipToName: "",
  qbAddressRaw: null,
  qbClassId: "",
  equipment: [],
  contacts: [],
  createdAt: new Date().toISOString(),
});

export const createEquipment = (type) => ({
  id: generateId(),
  type,
  category: "HVAC",
  subType: "",
  manufacturer: "",
  modelNumber: "",
  serialNumber: "",
  startupDate: "",
  warranties: [],
  status: "green",
  statusNote: "",
  statusChangedAt: "",
  statusChangedBy: "",
  statusChangedByName: "",
  statusChangedByOrg: "",
  quickNotes: "",
  startupInstructions: "",
  partsList: "",
  partsListPhotos: [],
  partsListFiles: [],
  lastPmDate: "",
  pmFrequencyDays: "",
  photos: [],
  serviceRecords: [],
  createdAt: new Date().toISOString(),
});

export const createServiceRecord = (notes) => ({
  id: generateId(),
  notes,
  date: new Date().toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" }),
  tag: "",
  photos: [],
  files: [],
  createdAt: new Date().toISOString(),
  deleted: false,
  deletedAt: null,
  deletedBy: null,
  auditTrail: [],
});

// A service ticket (work order) is a per-visit record a tech files against a
// job. One ticket can cover multiple employees (each with their own hours and
// date) so the customer signs ONE ticket, not one per tech. Materials carry a
// cost the tech entered — never shown to the customer. On submit, labor/
// materials feed draft lines into the job's Estimated Bill (Phase 4.2c).
export const createServiceTicket = () => ({
  id: generateId(),
  jobId: "",
  // denormalized from the job for dashboard filtering
  customerId: "",
  locationId: "",
  equipmentIds: [],
  workDate: new Date().toISOString().slice(0, 10),
  summary: "", // narrative: what was done
  // [{ id, employeeUid, employeeName, hours, date, payType }]
  //   payType: "straight" | "overtime" | "double" (same ids as the org's
  //   pay-type catalog). Defaults to "straight". Carried into the
  //   auto-generated timeEntry on sign-off so payroll's hours/dollars
  //   reflect OT/DT without a separate edit.
  labor: [],
  // [{ id, name, partNumber, qty, unitCost, source, poId, poItemId }]
  //   source:    "stock" | "credit_card" | "po"
  //   poId:      id of the linked purchase order (only when source === "po")
  //   poItemId:  id of the specific PO line item this material maps to
  //              (only when source === "po"). On selection, name / partNumber
  //              / unitCost are auto-copied from that PO item — the office
  //              copy can then show the price without re-typing it.
  // PO-sourced materials are tracked on the ticket for visibility but are
  // NOT fed into the bill — the PO itself already feeds those materials
  // via feedPOToBill. unitCost is hidden from the customer-facing copy.
  materials: [],
  photos: [],
  files: [],
  status: "draft", // draft | submitted | signed
  createdBy: "",
  createdByUid: "",
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  submittedAt: null,
  // signatures (Phase 4.2d): each { name, title, image (storage path/url), signedAt }
  contractorSignature: null,
  customerSignature: null,
  signedAt: null,
  // auto-feed bookkeeping (4.2c)
  fedToBill: false,
  // Idempotency flag for feedTicketTimeToBoard — set true once labor
  // rows are written to roster members' timeEntries (and non-roster
  // names appended to job.guestLabor). Re-saves of an already-signed
  // ticket won't re-write entries.
  fedToTimeBoard: false,
  // who a signed copy was sent to (4.2e)
  sentTo: [],
  // Edits made after the ticket was submitted but before it was signed.
  // Each entry: { ts, userEmail, userName, summary } — summary is a one-line
  // human-readable note ("Edited materials and summary"), not a diff.
  auditTrail: [],
});

// A vendor / supplier the contractor buys parts and services from.
// Per [[project-finance-ar-module]] Phase A0 + [[project-departments-as-capability-presets]]:
// gated by `manageVendors` capability; lives at
// organizations/{orgId}/vendors/{vendorId}. POs reference vendors by id;
// po.vendorName denormalized for history (same dual-write pattern as
// customerName on jobs).
export const createVendor = (name = "") => ({
  id: generateId(),
  name,                          // legal name on W-9 / 1099
  displayName: "",               // short label for dropdowns (defaults to name on save)
  dba: "",                       // doing-business-as (if different)
  street: "",
  city: "",
  state: "",
  zip: "",
  // Remit-to address — sometimes different from physical. Empty means
  // "use the address above" for printables.
  remitStreet: "",
  remitCity: "",
  remitState: "",
  remitZip: "",
  // Who we talk to. apContact gets POs / invoices come from them.
  // salesContact is who we phone to order parts.
  apContactName: "",
  apContactEmail: "",
  apContactPhone: "",
  salesContactName: "",
  salesContactEmail: "",
  salesContactPhone: "",
  accountNumber: "",             // OUR account # with them (goes on POs)
  // Payment terms enum: net30 | net15 | net10 | cod | "2_10_net30" | other
  paymentTerms: "net30",
  taxId: "",                     // EIN — required for 1099 reporting at year-end
  is1099Eligible: false,         // service vendors typically yes, goods vendors no
  w9OnFile: false,
  w9ExpiresOn: "",               // YYYY-MM-DD optional
  coiOnFile: false,              // certificate of insurance for subs / on-site work
  coiExpiresOn: "",              // YYYY-MM-DD optional
  categories: [],                // ["HVAC parts", "refrigerant", "tools", ...]
  status: "active",              // active | inactive | on_hold
  notes: "",
  createdAt: new Date().toISOString(),
  createdBy: "",
  createdByName: "",
  updatedAt: new Date().toISOString(),
  updatedBy: "",
  updatedByName: "",
});

// Payment method enum for AR mark-paid and AP record-payment (Phase B).
// Same enum on both sides — what showed up in the office is what showed
// up. "Other" is a free-text catch-all for crypto, wire, lockbox, etc.
export const PAYMENT_METHOD_OPTIONS = [
  { value: "check", label: "Check" },
  { value: "ach", label: "ACH / Bank transfer" },
  { value: "card", label: "Credit card" },
  { value: "cash", label: "Cash" },
  { value: "other", label: "Other (note recommended)" },
];

// Void reason enum for AR invoice voiding (Phase A). The bookkeeper picks
// the closest reason when voiding a bill; "other" requires a note. Real-
// world voids preserve the INV# for audit (the doc stays, just stamped
// voided) — see [[project-finance-ar-module]] Phase A spec.
export const VOID_REASON_OPTIONS = [
  { value: "wrong_customer", label: "Sent to wrong customer" },
  { value: "amount_incorrect", label: "Amount incorrect" },
  { value: "customer_dispute", label: "Customer dispute" },
  { value: "duplicate", label: "Duplicate invoice" },
  { value: "other", label: "Other (note required)" },
];

// Variance reason enum for vendor-bill verification (Phase A2). Categorical
// answer to "why doesn't the vendor's invoice match our PO total?" Surfaced
// in the variance picker on POsPage. "other" defaults to free-text note.
export const VARIANCE_REASON_OPTIONS = [
  { value: "price_change", label: "Vendor price change since PO" },
  { value: "short_ship", label: "Short ship (got fewer than ordered)" },
  { value: "backorder", label: "Backorder split — partial invoice" },
  { value: "restock_fee", label: "Restock fee added" },
  { value: "freight", label: "Freight / shipping added" },
  { value: "tax_diff", label: "Tax difference" },
  { value: "substitution", label: "Part substitution at different price" },
  { value: "other", label: "Other (note required)" },
];

export const PAYMENT_TERMS_OPTIONS = [
  { value: "net30", label: "Net 30" },
  { value: "net15", label: "Net 15" },
  { value: "net10", label: "Net 10" },
  { value: "cod", label: "COD (cash on delivery)" },
  { value: "2_10_net30", label: "2% 10 / Net 30" },
  { value: "prepay", label: "Prepay" },
  { value: "other", label: "Other (see notes)" },
];

// A purchase order — ordering parts/materials from a vendor for a job.
// Lifecycle: requested (tech request, no createPOs) → draft (issuer editing)
// → ordered (PO number assigned, sent to vendor) → received (locked; items
// auto-feed the job's Estimated Bill). Always tied to a job.
export const createPurchaseOrder = () => ({
  id: generateId(),
  poNumber: "", // PO-YYYY#### assigned when Marked Ordered
  jobId: "",
  customerId: "", // denormalized from the job
  locationId: "",
  // Vendor master reference. vendorId points at the vendor doc;
  // vendorName is denormalized + immutable on the PO so a vendor rename
  // doesn't rewrite history. Same dual-write pattern as customerId +
  // customerName elsewhere. Per Phase A0 (2026-06-15) — replaces the
  // pre-A0 free-text `vendor` field (Justin deleted existing POs before
  // A0 deploy so no migration needed).
  vendorId: "",
  vendorName: "",
  notes: "",
  // [{ id, name, partNumber, qty, unitCost }]
  items: [],
  // Lifecycle: requested | draft | ordered | received | bill_verified.
  // received = physical arrival (Justin's mental model: items showed up,
  // expect the bill next). bill_verified = vendorBill metadata entered +
  // matched. Phase B will add approved_for_payment | paid.
  status: "draft",
  expectedArrival: "",
  requestedBy: "", // email of the tech who requested it (if a request)
  orderedAt: null,
  receivedAt: null,
  fedToBill: false,
  // AP / vendor-bill tracking (Phase A2). Populated when finance enters
  // the vendor's invoice details. The PO subtotal is the source of truth
  // for the "we agreed to pay X" side; vendorBill.totalAmount is what
  // the vendor's actually charging. variance = what the bookkeeper
  // documents + investigates.
  vendorBill: {
    invoiceNumber: "",
    receivedDate: "",         // YYYY-MM-DD — when bill landed at office
    totalAmount: 0,
    varianceAmount: 0,        // signed: vendor-bill total minus PO subtotal
    variancePct: 0,
    // enum: price_change | short_ship | backorder | restock_fee | freight |
    // tax_diff | substitution | other
    varianceReason: "",
    varianceNote: "",
    verifiedAt: null,
    verifiedBy: "",
    verifiedByName: "",
  },
  auditTrail: [],
  createdAt: new Date().toISOString(),
  createdBy: "",
  createdByUid: "",
  updatedAt: new Date().toISOString(),
});

// A quote: a standalone, customer-facing price for a set scope of work,
// created before a job exists. Reuses the same line-item "costing" shape as
// the Estimated Bill. On customer approval (drawn signature) it auto-creates
// a linked job whose Estimated Bill is seeded from these line items.
export const createQuote = (title) => ({
  id: generateId(),
  // Auto-assigned at creation (Q-YYYY#### per-org, resets each year) by the
  // addQuote transaction in useOrgData.
  quoteNumber: "",
  title, // short description (search only — official ref is the quote number)
  // Drives the whole approve pipeline:
  //   "job"         — approve creates a one-shot Job (existing behavior)
  //   "maintenance" — approve creates a Contract + spawns visit jobs
  // Locked at creation time. To switch, delete and re-create.
  quoteType: "job",
  customerId: "",
  locationId: "",
  equipmentIds: [],
  scope: "", // customer-facing scope of work (newline-separated bullets)
  exclusions: "", // what's explicitly NOT in scope (newline-separated bullets)
  validDays: 30, // "quote good for N days"
  terms: "", // terms / conditions / notes shown on the quote (newline-separated bullets)
  // Price-centric line items (see QuoteEditor): labor hrs×price, materials &
  // charges qty×(cost×markup), taxes.
  costing: {
    labor: [],
    materials: [],
    charges: [],
    // Phase fuel-on-quote (Justin's ask 2026-06-15): trip / mileage line
    // items with the same mileage-calculator shape JobCosting uses. Each
    // entry: { id, mode: "mileage" | "flat", description, costPerMile,
    // milesOneWay, trips, amount, markup, taxable }. Renders in the quote
    // pricing block above taxes; flows into computeQuoteTotal so the
    // mirrored facility total and approve-quote pipelines pick it up.
    fuel: [],
    taxes: [],
  },
  // Maintenance-quote-only fields — meaningful only when quoteType === "maintenance".
  // When the customer signs the quote, these settings drive addContract:
  // the line-item subtotal becomes the ANNUAL price (not lifetime), term
  // years × visitsPerYear gives total visits, and the contract billingMode
  // controls when each visit's bill is generated. Defaults mirror
  // createContract() — quarterly visits, per-visit billing, 1-year term.
  contractTermYears: 1, // 1 | 2 | 3 | 5
  contractStartDate: "", // YYYY-MM-DD; defaults to today on the editor if blank
  contractVisitInterval: "quarterly", // monthly | quarterly | biannual | annual
  contractBillingMode: "per-visit", // per-visit | per-interval
  contractBillingInterval: "quarterly",
  // Annual escalator (%). 0 = flat pricing across the whole term (default).
  // Anything > 0 escalates year-N price = year-1 × (1 + pct/100)^(N-1).
  // Rides through to the contract on approveQuoteAndCreateContract.
  contractAnnualEscalationPct: 0,
  // Default tech for the resulting contract's visits (pre-QB runway #7).
  // Rides through to the contract on approveQuoteAndCreateContract.
  contractDefaultTechId: "",
  // Set when this quote replaces an expiring/expired contract (renewal flow).
  // Stamped into the new contract's renewedFrom on approval; the old contract
  // gets its renewedTo set in the same transaction.
  renewalOfContractId: null,
  status: "draft", // draft | sent | approved | declined
  sentAt: null,
  // Quote amendment trail. revision = 0 for the original; bumps each
  // time admin clicks "Amend Quote" on a sent quote (see useOrgData
  // amendQuote). revisionHistory[] keeps full snapshots of every prior
  // revision so a contractor can re-print any version. Same quote
  // number across all revisions -- customers see "Q-2026-0042 · Rev 2".
  // Approval locks the quote (no more amendments); post-approval
  // changes become change orders against the resulting job.
  revision: 0,
  revisionHistory: [],
  amendedAt: null,
  amendedBy: "",
  amendedByName: "",
  // Contractor signs electronically (typed) BEFORE sending — authorizes the
  // quote. { name, title, image, signedAt }.
  contractorSignature: null,
  // Customer signs by ink on the printed copy for now; in-app e-sign is a
  // future feature for facility customers who use the app.
  customerSignature: null,
  approvedAt: null,
  // Outcome pointers — exactly one of jobId / contractId is set on approval,
  // depending on quoteType.
  jobId: "",
  contractId: "",
  createdAt: new Date().toISOString(),
  createdBy: "",
  createdByUid: "",
  updatedAt: new Date().toISOString(),
});

/**
 * A maintenance contract (PM agreement) between the contractor and a
 * facility customer. Holds the term, billing model, scope, and covered
 * equipment. Visits under the contract are regular Job docs tagged with
 * `contractId` + `isMaintenanceVisit: true` + `visitIndex` + a
 * `scheduledDate` so they get all existing ticket / time / billing
 * infrastructure for free.
 *
 * Lives at organizations/{orgId}/contracts/{contractId}. Per spec
 * answers, scope is frozen at creation in Phase 1 (amendment workflow
 * is Phase 2). Renewal flows through the quote pipeline (Phase 4.D).
 */
export const createContract = (title = "") => ({
  id: generateId(),
  // Auto-assigned per-org (C-YYYY-####) via addContract transaction.
  contractNumber: "",
  title, // human label e.g. "Quarterly PM — Acme Headquarters"
  customerId: "",
  locationId: "",
  type: "PM", // "PM" | "FullService" (FullService is Phase 2)
  startDate: "", // YYYY-MM-DD
  termYears: 1, // 1 | 2 | 3 | 5
  endDate: "", // computed from startDate + termYears (one day before next year)
  annualPrice: 0,
  // Annual escalator (%). 0 = flat across the full term (default).
  // > 0 bumps year-N price by (1 + pct/100)^(N-1). Stamped onto each
  // visit's quotedAmount when spawned. See utils/contracts.js.
  annualEscalationPct: 0,
  // Default tech pre-assigned to every visit spawned under this contract
  // (pre-QB runway #7). Empty string = no default; visits spawn with
  // assignedTechs = []. Set to a user id and visits spawn with that
  // user pre-assigned. Field-eligible users only on the picker.
  defaultTechId: "",
  // Per Q1 answer: admin picks at contract creation, mode locked for life.
  //   "per-visit"     — bill when each visit is marked maintenance complete
  //                     (Justin's shop's actual practice: no labor, no money)
  //   "per-interval"  — bill on schedule regardless of visit status
  //                     (larger commercial / property mgmt style)
  billingMode: "per-visit",
  billingInterval: "quarterly", // monthly | quarterly | biannual | annual
  visitInterval: "quarterly",   // monthly | quarterly | biannual | annual
  scopeOfWork: "", // free-text scope (newline-separated bullets like quotes)
  equipmentIds: [], // covered units
  status: "active", // active | expired | cancelled
  // Renewal pointers (per Q4 — quotes pipeline drives renewals).
  renewedFrom: null,
  renewedTo: null,
  createdAt: new Date().toISOString(),
  createdBy: "",
  createdByName: "",
  // Cancellation metadata (per Q5 partial-billing cancellation reason rule
  // applies here too — capture reason on cancel for historical analytics).
  cancelledAt: null,
  cancelledBy: null,
  cancelReason: null,
});

export const createJob = (title) => ({
  id: generateId(),
  // Auto-assigned at creation (YYYY#### per-org, resets each year) by the
  // addJob transaction in useOrgData. Title stays as the human description.
  jobNumber: "",
  title,
  description: "",
  jobType: "", // Quoted | T&M | Maintenance | Warranty
  customerId: "",
  locationId: "",
  equipmentIds: [],
  // Maintenance contract linkage (only set on jobs spawned by addContract).
  // When set, this job IS a visit under a maintenance contract — admin
  // can find it via the contract's timeline, and it gets the maintenance-
  // specific lifecycle (Reschedule / Skip actions; "scheduled" status
  // means "future visit, not in active queue yet").
  contractId: null,
  visitIndex: null, // 1..N (which visit in the sequence)
  scheduledDate: "", // YYYY-MM-DD — when this visit is due
  isMaintenanceVisit: false,
  // Reschedule + skip metadata (Phase 4.B). auditTrail captures every
  // schedule change with old/new date + who/when/why. skipReason is set
  // when status flips to "skipped".
  visitAuditTrail: [],
  skipReason: "",
  // Boolean flag for work that isn't tied to a tracked equipment record
  // (lights, plumbing, general maintenance, etc.). Details live in the
  // job's description field.
  otherEquipment: false,
  // Set when the equipment/scope will be confirmed after an on-site
  // diagnostic visit (e.g. job created before the tech has walked the units).
  updateUponDiagnostic: false,
  // "open" is the umbrella for any non-closed job; these are its stages:
  //   unassigned | assigned | in_progress | closed | canceled
  // Maintenance visits add two:
  //   scheduled  — visit is on the contract schedule but not in the active
  //                queue yet (admin activates it when ready)
  //   skipped    — visit will not happen (customer no-show, equipment
  //                removed, etc.); doesn't get a bill in per-visit mode
  status: "unassigned",
  assignedTechs: [],
  // Partial / progress billing. A job either:
  //   (a) goes through the legacy single-finalize path (billFinalized
  //       fields below) — today's default for one-shot T&M jobs, or
  //   (b) accrues multiple bills via the Progress Bill flow (bills[]
  //       below) for big projects where equipment ships up front and
  //       labor lands months later.
  // bills[] entries look like:
  //   {
  //     id, type: "partial" | "final", description (required),
  //     billingMode: "line-item",       // percentage mode is Phase 2.B
  //     lineItemIds: [...],             // ids of costing rows on this bill
  //     subtotal, taxableSubtotal,
  //     taxes: [{ name, mode, rate, amount }], grandTotal,
  //     invoiceNumber: "INV-YYYY-####",
  //     finalizedAt, finalizedBy, finalizedByName,
  //   }
  // When a costing line is included on a finalized bill its billedOnInvoice
  // field carries that INV# so the next Progress Bill editor only shows
  // unbilled rows.
  bills: [],
  createdAt: new Date().toISOString(),
  createdBy: "",
  updatedAt: new Date().toISOString(),
  closedAt: null,
  closedBy: null,
  // Non-roster labor entries auto-appended by feedTicketTimeToBoard when
  // a signed ticket has an "Other (type in)" employee. Each entry:
  //   { name, hours, date, payType, sourceTicketId, feedAt }
  // Finance/payroll picks these up when that workflow is built.
  guestLabor: [],
});

// Facility-side service ticket. Lighter than the contractor version —
// just time + materials + notes + photos. No labor rates, no markups,
// no signatures. Org-private to the facility (contractor never sees).
export const createFacilityServiceTicket = (jobId) => ({
  id: generateId(),
  jobId,
  workDate: new Date().toISOString().slice(0, 10),
  summary: "",
  labor: [],     // [{ id, employeeName, hours, date }]
  materials: [], // [{ id, name, partNumber, qty }]
  notes: "",
  photos: [],    // [{ url, path, name }]
  acknowledged: false,
  acknowledgedAt: null,
  acknowledgedBy: null,
  createdAt: new Date().toISOString(),
  createdBy: "",
  createdByName: "",
});